// Utility layer (TS-2.5.04) - pure, no side effects. The Product-grouping half of the roadmap's
// Team/Product board toggle (decisions.md D-278, scoped by D-266's "Product stays entirely OUT of
// the Report Model" ruling): rebuilds the SAME server-derived board's swimlanes client-side by
// joining each chip's projectKey -> ProjectSummary.productKey -> ProductRecord.name, via the shared
// bucketing src/lib/productGrouping.ts now holds (D-277 - roadmap is the third feature to need this
// join, after features/products and features/projects).
//
// COLUMNS NEVER CHANGE (D-147/D-245): this returns the SAME `board.columns` array, untouched, and
// every chip object is reused as-is (same reference) - only which swimlane a chip's own object sits
// in is rebuilt. Lane order matches the shared bucketing: assigned products alphabetically by name,
// then exactly one Unassigned lane last, even when empty (mirrors D-272's Products-page convention
// rather than inventing a different one for the board).
import type { BoardChip, ProductRecord, ProjectSummary, RoadmapBoard as RoadmapBoardModel, Swimlane } from "../../../api/generated/contracts";
import { groupByProduct } from "../../../lib/productGrouping";

export function regroupBoardByProduct(
  board: RoadmapBoardModel,
  projects: readonly ProjectSummary[],
  products: readonly ProductRecord[],
  unassignedLabel: string,
): RoadmapBoardModel {
  const productKeyByProjectKey = new Map(projects.map((project) => [project.projectKey, project.productKey]));

  // Flattened once: today a chip belongs to exactly one team swimlane, so this cannot duplicate a
  // chip across lanes - it only changes which (now product-named) lane it lands in.
  const chips: BoardChip[] = board.swimlanes.flatMap((lane) => lane.chips);

  const buckets = groupByProduct(chips, products, (chip) => productKeyByProjectKey.get(chip.projectKey) ?? null);

  const swimlanes: Swimlane[] = buckets.map((bucket, index) => ({
    teamGroup: bucket.product?.name ?? unassignedLabel,
    displayOrder: index + 1,
    chips: [...bucket.items],
  }));

  return { columns: board.columns, swimlanes };
}
